/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Why the charge was refused, in the host's own words.
 *
 * Same boundary the coupon already keeps (`Checkout.coupon.ts`): the code
 * crosses the frame, never the sentence. Payien names the reason and the
 * host says it, so the page stays in one voice and one language.
 *
 * Before this existed every refusal read "Não foi possível iniciar o
 * pagamento. Tente novamente." — the key `Payment.tsx` uses when the
 * *session* could not be built. So a card the issuer declined was reported
 * as an outage, and the one thing the buyer could have acted on — the card —
 * was the one thing the sentence did not mention (BUG-027).
 */

/**
 * Payien's refusal codes, mapped to what the buyer is told.
 *
 * The codes are the ones `secure_checkout_subscription_only.html` emits on
 * `payment_failed`. A code absent here is not a bug on this side: the
 * fallback says the charge did not go through without inventing a reason
 * for it.
 */
const PAYMENT_ERROR_KEYS: Record<string, string> = {
  // The card itself is wrong or unreadable. Another card, or the same one
  // typed correctly, is the way out.
  tokenization_failed: 'checkoutStep.payment.declined.cardInvalid',
  cc_rejected_bad_filled_card_number: 'checkoutStep.payment.declined.cardNumber',
  cc_rejected_bad_filled_security_code: 'checkoutStep.payment.declined.cvv',
  cc_rejected_bad_filled_date: 'checkoutStep.payment.declined.expiry',
  cc_rejected_bad_filled_other: 'checkoutStep.payment.declined.cardInvalid',
  cc_rejected_insufficient_data: 'checkoutStep.payment.declined.cardInvalid',
  cc_rejected_card_error: 'checkoutStep.payment.declined.cardInvalid',

  // The card is readable and the issuer said no.
  cc_rejected_insufficient_amount: 'checkoutStep.payment.declined.insufficientFunds',
  cc_rejected_call_for_authorize: 'checkoutStep.payment.declined.callIssuer',
  cc_rejected_card_disabled: 'checkoutStep.payment.declined.cardDisabled',
  cc_rejected_duplicated_payment: 'checkoutStep.payment.declined.duplicated',
  cc_rejected_high_risk: 'checkoutStep.payment.declined.highRisk',
  cc_rejected_invalid_installments: 'checkoutStep.payment.declined.other',
  cc_rejected_max_attempts: 'checkoutStep.payment.declined.maxAttempts',
  cc_rejected_other_reason: 'checkoutStep.payment.declined.other',
  payment_declined: 'checkoutStep.payment.declined.other',
  Invalid_payment_method: 'checkoutStep.payment.declined.unsupportedCard',

  // Nothing about the card is the problem, so none of these invite another
  // one — the frame marks them `changeable: false` for the same reason.
  missing_organization_document: 'checkoutStep.payment.declined.registrationDocument',
  missing_organization_email: 'checkoutStep.payment.declined.registrationEmail',
  organization_already_subscribed: 'checkoutStep.payment.declined.alreadySubscribed',
  session_consumed: 'checkoutStep.payment.declined.sessionConsumed',
  session_processing: 'checkoutStep.payment.declined.sessionProcessing',
  customer_error: 'checkoutStep.payment.declined.generic',
};

/** What is said when Payien named no code, or one this host has not met. */
const PAYMENT_ERROR_FALLBACK = 'checkoutStep.payment.declined.generic';

/**
 * Said after the reason, when trying another card is actually worth doing.
 *
 * Kept apart from the reason rather than folded into each sentence: the
 * frame decides changeability per code, and twenty-three sentences each
 * carrying their own copy of that advice would drift from its answer.
 */
const PAYMENT_ERROR_RETRY = 'checkoutStep.payment.declined.tryAnother';

/**
 * The translation key for a refusal.
 *
 * @param errorCode - Payien's code for why the charge did not go through.
 * @returns The onboarding key to show, or the generic one.
 */
export const paymentErrorKey = (errorCode: string): string => (
  PAYMENT_ERROR_KEYS[errorCode] ?? PAYMENT_ERROR_FALLBACK
);

/**
 * What the buyer reads when the charge was refused.
 *
 * @param errorCode - Payien's code for the refusal.
 * @param changeable - Whether the frame says another card could work.
 * @param t - The onboarding translator.
 * @returns One sentence, plus the invitation to retry when there is one.
 */
export const paymentErrorMessage = (
  errorCode: string,
  changeable: boolean,
  t: (key: string) => string,
): string => {
  const reason = t(paymentErrorKey(errorCode));
  return changeable ? `${reason} ${t(PAYMENT_ERROR_RETRY)}` : reason;
};
